/* eslint-disable import/prefer-default-export */
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, CircularProgress } from '@mui/material';
import pMinDelay from 'p-min-delay';
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';

const DEFAULT_VARIANTS = {
    default: {
        confirmButton: {
            label: 'Confirm',
            color: 'primary',
        },
        cancelButton: {
            color: 'inherit',
        },
    },
    delete: {
        confirmButton: {
            label: 'Delete',
            color: 'error',
        },
        cancelButton: {
            color: 'inherit',
        },
    },
};

function ConfirmationDialogComponent({ open, onClose, onConfirm, title, message, variant, asyncFn, customVariants }) {
    const [loading, setLoading] = useState(false);

    const variants = useMemo(() => ({ ...DEFAULT_VARIANTS, ...customVariants }), [customVariants]);
    const selectedVariant = variants[variant] ?? variants.default;

    useEffect(() => {
        if (!open) {
            setLoading(false);
        }
    }, [open]);

    const handleConfirm = useCallback(async () => {
        if (asyncFn) {
            setLoading(true);
            try {
                const result = await pMinDelay(asyncFn.fn(), asyncFn.minDelay);
                onConfirm(result);
            } catch (error) {
                onConfirm(error);
            } finally {
                setLoading(false);
            }
        } else {
            onConfirm(true);
        }
    }, [asyncFn, onConfirm]);

    const handleCancel = useCallback(() => {
        if (!loading) {
            onClose();
        }
    }, [loading, onClose]);

    return (
        <Dialog open={open} onClose={handleCancel} disableEscapeKeyDown={loading}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <DialogContentText>{message}</DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleCancel}
                    color={selectedVariant?.cancelButton?.color}
                    sx={selectedVariant?.cancelButton?.sx}
                    variant="outlined"
                    disabled={loading}
                    // wrap
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    color={selectedVariant?.confirmButton?.color}
                    sx={selectedVariant?.confirmButton?.sx}
                    autoFocus
                    variant="contained"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} /> : null}
                    // wrap
                >
                    {selectedVariant?.confirmButton?.label || 'Confirm'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/**
 * A React component that renders the confirmation dialog UI.
 * @typedef {React.ComponentType} ConfirmationDialogComponent
 *
 * Shows the confirmation dialog and returns the users response.
 * @typedef {(message: string, title?: string, variant?: string) => Promise<boolean>} ConfirmFunction
 * @param {string} message - The message to display in the confirmation dialog
 * @param {string} [title='Confirm Action'] - The title of the confirmation dialog
 * @param {string} [variant='default'] - The variant to use for styling
 * @returns {Promise<boolean>} A Promise that resolves to true if confirmed, false if cancelled
 *
 * Configuration object for confirmAsync function.
 * @typedef {Object} ConfirmAsyncConfig
 * @property {string} message - The message to display in the confirmation dialog
 * @property {() => Promise<any>} asyncFn - The async function to execute on confirm
 * @property {string} [title='Confirm Action'] - The title of the confirmation dialog
 * @property {string} [variant='default'] - The variant to use for styling
 * @property {number} [minDelay=500] - Minimum delay in milliseconds to show loading state
 *
 * Shows the confirmation dialog, executes async function on confirm, and returns the result.
 * @typedef {(config: ConfirmAsyncConfig) => Promise<any>} ConfirmAsyncFunction
 * @param {ConfirmAsyncConfig} config - Configuration object
 * @returns {Promise<any>} A Promise that resolves to the result of asyncFn, or false if cancelled
 *
 * @typedef {Object} ConfirmationDialogHookResult
 * @property {ConfirmFunction} confirm - Function to show the confirmation dialog and get user response
 * @property {ConfirmAsyncFunction} confirmAsync - Function to show the confirmation dialog and execute async function
 * @property {ConfirmationDialogComponent} ConfirmationDialog - React component to render the dialog
 */

/**
 * Custom hook that provides a confirmation dialog with Promise-based API.
 *
 * @returns {ConfirmationDialogHookResult} The confirmation dialog controller
 *
 * @example
 * const { confirm, confirmAsync, ConfirmationDialog } = useConfirmationDialog();
 *
 * // Basic confirmation:
 * const handleDelete = async () => {
 *   const confirmed = await confirm('Are you sure you want to delete this item?', 'Delete Item', 'delete');
 *   if (confirmed) {
 *     // Proceed with deletion
 *   }
 * };
 *
 * // Async confirmation with loading state:
 * const handleAsyncDelete = async () => {
 *   const result = await confirmAsync({
 *     message: 'Are you sure you want to delete this item?',
 *     asyncFn: () => deleteItemAPI(itemId),
 *     title: 'Delete Item',
 *     variant: 'delete',
 *     minDelay: 800 // Custom minimum delay
 *   });
 *   if (result !== false) {
 *     // Handle successful deletion result
 *   }
 * };
 *
 * // In your JSX:
 * return (
 *   <>
 *     <Button onClick={handleDelete}>Delete</Button>
 *     <Button onClick={handleAsyncDelete}>Async Delete</Button>
 *     <ConfirmationDialog />
 *   </>
 * );
 */
export function useConfirmationDialog({ customVariants } = {}) {
    const [open, setOpen] = useState(false);
    const [dialogProps, setDialogProps] = useState({
        title: '',
        message: '',
        variant: 'default',
        asyncFn: null,
    });

    const resolveRef = useRef(null);

    const confirm = useCallback((customMessage, customTitle = 'Confirm Action', customVariant = 'default') => {
        return new Promise((resolve) => {
            resolveRef.current = resolve;
            setDialogProps({
                title: customTitle,
                message: customMessage,
                variant: customVariant,
                asyncFn: null,
            });
            setOpen(true);
        });
    }, []);

    const confirmAsync = useCallback((config) => {
        const {
            message: customMessage,
            asyncFn: asyncFunction,
            title: customTitle = 'Confirm Action',
            variant: customVariant = 'default',
            minDelay = 500,
            // wrap
        } = config;

        return new Promise((resolve) => {
            resolveRef.current = resolve;
            setDialogProps({
                title: customTitle,
                message: customMessage,
                variant: customVariant,
                asyncFn: { fn: asyncFunction, minDelay },
            });
            setOpen(true);
        });
    }, []);

    const handleClose = useCallback(() => {
        if (resolveRef.current) {
            resolveRef.current(false);
        }
        resolveRef.current = null;
        setOpen(false);
    }, []);

    const handleConfirm = useCallback((result) => {
        if (resolveRef.current) {
            resolveRef.current(result);
        }
        resolveRef.current = null;
        setOpen(false);
    }, []);

    const ConfirmationDialog = useCallback(
        () => (
            <ConfirmationDialogComponent
                open={open}
                onClose={handleClose}
                onConfirm={handleConfirm}
                title={dialogProps.title}
                message={dialogProps.message}
                variant={dialogProps.variant}
                asyncFn={dialogProps.asyncFn}
                customVariants={customVariants}
                // wrap
            />
        ),
        [open, handleClose, handleConfirm, dialogProps, customVariants]
    );

    return { confirm, confirmAsync, ConfirmationDialog };
}
